import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { useDeleteTmaisBadge } from '@/features/tmaisBadges/hooks/useDeleteTmaisBadge'
import { useTmaisBadge } from '@/features/tmaisBadges/hooks/useTmaisBadge'
import { TmaisBadge } from '@/features/tmaisBadges/types/tmaisBadge.type'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronsRight } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { useGoBack } from '@/common/hooks/useGoBack'
import { useState } from 'react' 
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { FormTransformers } from '@/common/utils/formTransformers' 

const ShowTmaisBadge = () => {
  const { id } = useParams<{ id: string }>()
  const goBack = useGoBack() 
  const queryClient = useQueryClient()
  
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const canDelete = isUserPermissionsPending || hasUserPermission('Admin::Tmais::Badges::Destroy')
  const canUpdate = isUserPermissionsPending || hasUserPermission('Admin::Tmais::Badges::Update')

  const {
    data: badge,
    isPending,
    error: badgeError = {} as any,
    refetch,
  } = useTmaisBadge<TmaisBadge>({ id })

  const { mutate: deleteBadge, isPending: deleteIsPending } = useDeleteTmaisBadge({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tmaisBadges'] })
      queryClient.removeQueries({ queryKey: ['tmaisBadge', id] })
      goBack({ fallback: '/tmais-badges' })
    },
    onError: FormTransformers.errorsTransformer({ setMessages }), 
  })

  const handleDelete = () => {
    if (!id || !badge || badge.discarded_at || !canDelete) return
    deleteBadge({ id })
  }

  const disableEditButton = isPending || !badge
  const disableDeleteButton = deleteIsPending || !badge
  const shouldRenderDeleteButton = !!badge && !badge.discarded_at && canDelete
  const editLink = canUpdate && !disableEditButton && id ? `/tmais-badges/${id}/edit` : ''

  const editButton = (
    <Button
      className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
      disabled={disableEditButton}
      operationKlassToCheckPermission="Admin::Tmais::Badges::Update"
    >
      Editar
    </Button>
  )

  const previewBackground = badge?.background_color || '#1F2933'
  const previewTextColor = badge?.text_color || '#FFFFFF'

  return (
    <div className="flex flex-col gap-6 pb-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GoBackButton fallback="/tmais-badges" searchLastRouteFromFallback />
          <p className="text-white-400 font-normal">Destaques para itens</p>
          <ChevronsRight className="text-white-400" />
          <h2>{badge?.display_name || 'Detalhes do destaque'}</h2>
        </div>

        <div className="flex gap-3">
          {shouldRenderDeleteButton && (
            <Button
              variant="error"
              disabled={disableDeleteButton}
              isLoading={deleteIsPending}
              onClick={handleDelete}
              operationKlassToCheckPermission="Admin::Tmais::Badges::Destroy"
            >
              Desativar
            </Button>
          )}

          {editLink ? (
            <Link to={editLink}>
              {editButton}
            </Link>
          ) : (
            editButton
          )}
        </div>
      </div>

      <ErrorMessageWrapper
        className="max-h-[calc(100vh-128px)]"
        statusCode={badgeError?.status}
        refetchFunction={refetch}
      >
        {isPending ? (
          <Box>Carregando...</Box>
        ) : (
          <Box className="flex flex-col gap-6 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
            <p className="text-lg font-bold">Detalhes do Destaque de item</p>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px,1fr] items-stretch">
              <div className="flex flex-col gap-2 h-full">
                <div className="flex-1 w-full bg-neutral-op-8 rounded-md flex flex-col items-center justify-center p-6 border border-transparent min-h-[220px]">
                  {badge?.icon?.url ? (
                    <img
                      src={badge.icon.url}
                      alt={`Ícone do destaque ${badge.display_name}`}
                      className="h-32 w-32 object-contain drop-shadow-md"
                    />
                  ) : (
                    <div className="flex h-32 w-32 items-center justify-center rounded-lg bg-white-200 text-white-400">
                      Sem ícone
                    </div>
                  )}
                  <p className="text-xs text-white-400 mt-4 text-center">Imagens/GIF 1:1 (mínimo 64x64)</p>
                </div>
              </div>

              <div className="flex flex-col gap-5 justify-between">
                
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-semibold text-white-400">Nome do destaque</p>
                  <p className="rounded-md border border-transparent bg-neutral-op-8 p-3 text-sm h-[46px] flex items-center">
                    {badge?.display_name || '--'}
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <p className="text-xs font-semibold text-white-400">Cor do background</p>
                    <div className="flex items-center gap-3 rounded-md border border-transparent bg-neutral-op-8 p-3 h-[46px]">
                      <span className="size-5 rounded-full border border-white/20 shadow-sm" style={{ backgroundColor: badge?.background_color }} />
                      <span className="text-sm font-semibold uppercase">{badge?.background_color || '--'}</span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <p className="text-xs font-semibold text-white-400">Cor da fonte</p>
                    <div className="flex items-center gap-3 rounded-md border border-transparent bg-neutral-op-8 p-3 h-[46px]">
                      <span className="size-5 rounded-full border border-white/20 shadow-sm" style={{ backgroundColor: badge?.text_color }} />
                      <span className="text-sm font-semibold uppercase">{badge?.text_color || '--'}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <p className="text-xs font-semibold text-white-400">Status</p>
                    <span className="px-3 py-1 text-sm font-semibold rounded-full w-fit mt-1 bg-neutral-op-8 text-white">
                      {badge?.discarded_at ? 'Inativo' : 'Ativo'}
                    </span>
                  </div>
                </div>

                <div className="flex flex-row items-center gap-4 mt-2">
                  <p className="text-base font-bold">Preview do app</p>
                  <div
                    className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 min-h-[32px] w-fit max-w-full overflow-hidden"
                    style={{ backgroundColor: previewBackground, color: previewTextColor }}
                  >
                    {badge?.icon?.url ? (
                      <img src={badge.icon.url} alt="Pré-visualização" className="size-4 object-contain" />
                    ) : null}
                    <span className="text-xs font-semibold whitespace-nowrap">
                      {badge?.display_name || 'Nome do destaque'}
                    </span>
                  </div>
                </div>

              </div>
            </div>
          </Box>
        )}
      </ErrorMessageWrapper>
      {messages && (
        <ModalMessageSimple 
          messages={messages} 
          setMessages={setMessages} 
          isError 
        />
      )}
    </div>
  )
}

export default ShowTmaisBadge
